import './styles/LocationModal.scss';

import { Grid } from '@mui/material';
import { CreateLocation, Location } from '@thro-types/leihbar';

import { Formik } from 'formik';
import { omit, pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useLocationMutation } from 'api/hooks/leihbar';
import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { validateForm } from 'utils/FormErrorHelper';

import { LocationInformation } from './components/LocationInformation';
import { locationValidationSchema } from './validation';

interface Props {
  closeHandler: Function;
  setDisabledCancel: Function;
  location: Location | undefined;
}

export function LocationModal(props: Props) {
  const [submitting, setSubmitting] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { create, update } = useLocationMutation();

  const initialValues = () => {
    return props.location ? { ...pick(props.location, ['id', 'name']) } : { name: '' };
  };

  const handleSaveClick = async (values: CreateLocation & { id?: number }) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }

    const omitFields = ['id'] as const;
    let saveBody = omit(values, ...omitFields);

    if (values.id) {
      try {
        await update.mutateAsync({
          id: values.id,
          data: saveBody
        });
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.location.alerts.successfulUpdate()))
        );
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.location.alerts.failedUpdate())));
        setSubmitting(false);
      }
    } else {
      try {
        await create.mutateAsync(saveBody);
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.location.alerts.successfulCreate()))
        );
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.location.alerts.failedCreate())));
        setSubmitting(false);
      }
    }
    props.closeHandler();
  };

  const steps = ['Information'];

  const SettingsModalStep = ({ step }) => {
    switch (step) {
      case 0:
        return <LocationInformation />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="locationModal">
      <Formik
        initialValues={initialValues()}
        onSubmit={values => handleSaveClick(values)}
        validationSchema={locationValidationSchema(t)}
        validate={values => {
          return validateForm(values, locationValidationSchema(t), dispatch);
        }}
      >
        <MultistepForm
          submitting={submitting}
          steps={steps}
          onCancel={props.closeHandler}
          hideStepper
        >
          {activeStep => (
            <Grid container className="locationModal__body">
              <SettingsModalStep step={activeStep} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
